/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Sheet,
  Tooltip,
  Typography,
} from '@mui/joy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getShipmentPiecesForShipment } from '../../../main/API/shipmentPiece';
import { fetchAllPages } from '../../../main/utils/pagination';
import {
  shipmentStatusChipSx,
  shipmentStatusJoyColor,
  shipmentStatusLabel,
} from '../../../main/utils/shipmentStatusDisplay';
import ShipmentPiecesTable from './ShipmentPiecesTable';

/**
 * Returns true when at least two pieces carry distinct non-null
 * `meter_price_override` values. The engine falls back to the shipment-level
 * override in that case (per FRONTEND_PER_PIECE_WEIGHT_PRICING_GUIDE §7.1).
 */
function hasOverrideConflict(pieces) {
  if (!Array.isArray(pieces)) return false;
  const seen = new Set();
  for (const p of pieces) {
    const v = p && p.meter_price_override;
    if (v == null || v === '') continue;
    seen.add(String(v));
    if (seen.size > 1) return true;
  }
  return false;
}

/**
 * Renders the "Pieces summary" panel for a shipment detail / edit form.
 *
 * Props:
 *  - shipmentId: number — required when `pieces` is empty so we can drill down via API.
 *  - pieces: ShipmentPieceSummary[] — nested array from the shipment detail payload.
 *  - piecesStatusSummary: { total, by_status, by_event_type } — optional summary chips.
 *  - drillDownThreshold: number — at or above this count we fetch via the paginated API.
 *  - onChanged: () => void — called when a piece is created/modified so the parent
 *    shipment can refetch and pick up the rolled-up totals (weight, chargeable_weight,
 *    packages_quantity, charge, price).
 */
export default function ShipmentPiecesPanel({
  shipmentId,
  pieces = [],
  piecesStatusSummary = null,
  drillDownThreshold = 20,
  onChanged,
}) {
  const { t } = useTranslation('ui');

  const initialPieces = useMemo(() => (Array.isArray(pieces) ? pieces : []), [pieces]);
  const totalPieces = useMemo(() => {
    if (piecesStatusSummary && typeof piecesStatusSummary.total === 'number') {
      return piecesStatusSummary.total;
    }
    return initialPieces.length;
  }, [piecesStatusSummary, initialPieces]);

  const needsDrillDown = totalPieces >= drillDownThreshold;
  const [items, setItems] = useState(initialPieces);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setItems(initialPieces);
  }, [initialPieces]);

  const loadDrillDown = useCallback(async () => {
    if (!shipmentId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await fetchAllPages(
        (page, pageSize) => getShipmentPiecesForShipment(shipmentId, { page, page_size: pageSize }),
      );
      setItems(list);
    } catch (e) {
      setError(t('shipmentPieces.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [shipmentId, t]);

  useEffect(() => {
    if (needsDrillDown && shipmentId) {
      loadDrillDown();
    }
  }, [needsDrillDown, shipmentId, loadDrillDown]);

  const handleRefresh = useCallback(async () => {
    if (needsDrillDown && shipmentId) {
      await loadDrillDown();
    }
    if (typeof onChanged === 'function') onChanged();
  }, [needsDrillDown, shipmentId, loadDrillDown, onChanged]);

  const summaryChips = useMemo(() => {
    if (!piecesStatusSummary || !piecesStatusSummary.by_status) return null;
    const entries = Object.entries(piecesStatusSummary.by_status).filter(
      ([, count]) => Number(count) > 0
    );
    return entries.map(([key, count]) => (
      <Chip
        key={`s-${key}`}
        size="sm"
        color={shipmentStatusJoyColor(key)}
        variant="soft"
        sx={shipmentStatusChipSx(key)}
      >
        {shipmentStatusLabel(key, t)}: {count}
      </Chip>
    ));
  }, [piecesStatusSummary, t]);

  const overrideConflict = useMemo(() => hasOverrideConflict(items), [items]);

  return (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography level="title-md" sx={{ flex: 1 }}>
          {t('shipmentPieces.summary.title')}
        </Typography>
        <Chip variant="soft" color="primary">
          {t('shipmentPieces.summary.total')}: {totalPieces}
        </Chip>
        {summaryChips}
        {needsDrillDown && (
          <Tooltip title={t('actions.refresh')}>
            <IconButton size="sm" variant="plain" onClick={loadDrillDown} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {overrideConflict ? (
        <Alert color="warning" variant="soft" sx={{ mb: 1.5 }}>
          {t('shipmentPieces.overrideConflictWarning', {
            defaultValue: 'Pieces have different per-piece overrides — the engine will use the shipment-level override or the rule price. Align the piece overrides or set the shipment-level override to control pricing.',
          })}
        </Alert>
      ) : null}

      {error && (
        <Typography level="body-sm" color="danger" sx={{ py: 2, textAlign: 'center' }}>
          {error}
        </Typography>
      )}

      {!error && !loading && items.length === 0 && (
        <Typography level="body-sm" sx={{ color: 'text.tertiary', py: 1, textAlign: 'center' }}>
          {t('shipmentPieces.noPieces')}
        </Typography>
      )}

      {!error && (
        <ShipmentPiecesTable
          rows={items}
          loading={loading}
          lockedShipmentId={shipmentId}
          onRefresh={handleRefresh}
          hideShipmentColumn
        />
      )}
    </Sheet>
  );
}
